import { Levensverzekering } from "../../../../.generated/rapportage/rapportagetypes";
import { Column } from "../../components/BaseTable";
import { bedragFormat } from "../../../../shared/utils/currency";
import { getFormattedDateFromString } from "../../../../shared/utils/dates";
import { formatDuration, formatPercentage } from "../../../formatters";

export const getLevensverzekeringColumns = (
  levensverzekering: Levensverzekering,
  isHuidig?: boolean
): Column[] => {
  const cols: Column[] = [
    {
      header: "Kapitaalverzekering",
      accessor: () => " ",
    },

    {
      header: "Lopend of nieuwe polis",
      accessor: isHuidig ? () => "Bestaande polis" : "lopendeOfNieuwePolis",
    },
    {
      header: "Verzekeraar",
      accessor: "verzekeraar",
    },

    {
      header: "Productnaam",
      accessor: "productnaam",
    },

    {
      header: "Ingangsdatum",
      accessor: "ingangsdatum",

      dataType: "date",
    },
    {
      header: "Einddatum verzekering, looptijd",
      accessor: (data: Levensverzekering) => {
        return data.einddatum
          ? `${getFormattedDateFromString(data.einddatum)}, ${data.looptijd}`
          : null;
      },
    },
    {
      header: "Verzekeringnemer 1",
      accessor: (data: Levensverzekering) => {
        return data?.verzekeringnemers && data.verzekeringnemers.length > 0
          ? data.verzekeringnemers[0]
          : null;
      },
    },
    {
      header: "Verzekeringnemer 2",
      accessor: (data: Levensverzekering) => {
        return data?.verzekeringnemers && data.verzekeringnemers.length > 1
          ? data.verzekeringnemers[1]
          : null;
      },
    },
    {
      header: "Soort verzekering",
      accessor: "soortVerzekering",
    },
  ];

  if (
    levensverzekering.verzekerde1Overlijdensrisicodekking ||
    levensverzekering.verzekerde2Overlijdensrisicodekking
  ) {
    cols.push({
      header: " ",
      accessor: () => " ",
    });
  } else {
    cols.push(
      {
        header: "Verzekerde 1",
        accessor: (data: Levensverzekering) => {
          return data.verzekerden?.[0] ?? null;
        },
      },
      {
        header: "Verzekerde 2",
        accessor: (data: Levensverzekering) => {
          return data.verzekerden?.[1] ?? null;
        },
      }
    );
  }

  if (levensverzekering.verzekerde1Overlijdensrisicodekking) {
    cols.push(
      {
        header: "Verzekerde",
        accessor: (data: Levensverzekering) => {
          return data.verzekerden?.[0] ?? null;
        },
        className: "bold",
      },
      {
        header: "Overlijdensrisicodekking",
        accessor: (data: Levensverzekering) => {
          return data.verzekerde1Overlijdensrisicodekking
            ?.verzekerdKapitaalBedrag
            ? bedragFormat(
                data.verzekerde1Overlijdensrisicodekking
                  .verzekerdKapitaalBedrag,
                0
              )
            : data.verzekerde1Overlijdensrisicodekking
                ?.verzekerdKapitaalTekst ?? null;
        },
        dataType: "currency",
      },
      {
        header: "Soort overlijdensrisicodekking",
        accessor: (data: Levensverzekering) =>
          data.verzekerde1Overlijdensrisicodekking?.soort ?? null,
      },
      {
        header: "Dekking daalt, duur daling",
        accessor: (data: Levensverzekering) => {
          return data.verzekerde1Overlijdensrisicodekking?.dekkingDaaltTot
            ? `${bedragFormat(
                data.verzekerde1Overlijdensrisicodekking.dekkingDaaltTot,
                0
              )}, ${data.verzekerde1Overlijdensrisicodekking
                .duurDalingInJaren ?? 0 / 12} maanden`
            : null;
        },
      },
      {
        header: "Partner rente",
        accessor: (data: Levensverzekering) => {
          return data.partnerrenteAanvragerBedrag
            ? `${bedragFormat(data.partnerrenteAanvragerBedrag, 0)}, ${
                data.partnerrenteAanvragerDuur
              } maanden`
            : null;
        },
      },
      {
        header: "Premievrijstelling bij arbeidsongeschiktheid",
        accessor: (data: Levensverzekering) => {
          const premievrijstelling =
            data.verzekerde1Overlijdensrisicodekking?.premievrijstellingBijAo;

          if (premievrijstelling === null) {
            return null;
          }

          return premievrijstelling ? "Ja" : "Nee";
        },
      }
    );
  }

  if (levensverzekering.verzekerde2Overlijdensrisicodekking) {
    cols.push(
      {
        header: "Verzekerde",
        accessor: (data: Levensverzekering) => {
          return data.verzekerden?.[1] ?? null;
        },
        className: "bold",
      },
      {
        header: "Overlijdensrisicodekking",
        accessor: (data: Levensverzekering) => {
          if (
            data.verzekerde2Overlijdensrisicodekking?.verzekerdKapitaalBedrag
          ) {
            return bedragFormat(
              data.verzekerde2Overlijdensrisicodekking.verzekerdKapitaalBedrag,
              0
            );
          } else if (
            data.verzekerde2Overlijdensrisicodekking?.verzekerdKapitaalTekst ===
            ""
          ) {
            return null;
          } else {
            return (
              data.verzekerde2Overlijdensrisicodekking
                ?.verzekerdKapitaalTekst ?? null
            );
          }
        },
        dataType: "currency",
      },
      {
        header: "Soort overlijdensrisicodekking",
        accessor: (data: Levensverzekering) =>
          data.verzekerde2Overlijdensrisicodekking?.soort ?? null,
      },
      {
        header: "Dekking daalt, duur daling",
        accessor: (data: Levensverzekering) => {
          return data.verzekerde2Overlijdensrisicodekking?.dekkingDaaltTot
            ? `${bedragFormat(
                data.verzekerde2Overlijdensrisicodekking.dekkingDaaltTot,
                0
              )}, ${data.verzekerde2Overlijdensrisicodekking
                .duurDalingInJaren ?? 0 / 12} maanden`
            : null;
        },
      },
      {
        header: "Partner rente",
        accessor: (data: Levensverzekering) => {
          return data.partnerrenteAanvragerBedrag
            ? `${bedragFormat(data.partnerrenteAanvragerBedrag, 0)}, ${
                data.partnerrenteAanvragerDuur
              } maanden`
            : null;
        },
      },
      {
        header: "Premievrijstelling bij arbeidsongeschiktheid",
        accessor: (data: Levensverzekering) => {
          const premievrijstelling =
            data.verzekerde2Overlijdensrisicodekking?.premievrijstellingBijAo;

          if (premievrijstelling === null) {
            return null;
          }

          return premievrijstelling ? "Ja" : "Nee";
        },
      }
    );
  }

  if (
    levensverzekering.verzekerde1Overlijdensrisicodekking ||
    levensverzekering.verzekerde2Overlijdensrisicodekking
  ) {
    cols.push({
      header: " ",
      accessor: () => " ",
    });
  }

  cols.push(
    {
      header: "Doelkapitaal, rendement",
      accessor: (data: Levensverzekering) => {
        return data.doelkapitaal
          ? `${bedragFormat(data.doelkapitaal, 0)}, ${formatPercentage(
              data.doelkapitaalRendement,
              2
            )}`
          : null;
      },
    },
    {
      header: "Garantiekapitaal, rendement",
      accessor: (data: Levensverzekering) => {
        return data.garantieKapitaal
          ? `${bedragFormat(data.garantieKapitaal, 0)}, ${formatPercentage(
              data.garantieKapitaalRendement,
              2
            )}`
          : null;
      },
    },
    {
      header: "Voorbeeldkapitaal",
      accessor: (data: Levensverzekering) =>
        data.voorbeeldkapitaal
          ? `${bedragFormat(data.voorbeeldkapitaal, 0)}, ${formatPercentage(
              data.voorbeeldkapitaalRendement,
              2
            )}`
          : null,
    },
    {
      header: "Aanvangsstorting",
      accessor: "aanvangsstoring",
      dataType: "currency",
    },
    {
      header: "(Lage) premie, betalingstermijn",
      accessor: (data: Levensverzekering) =>
        data.lagePremie
          ? `${bedragFormat(data.lagePremie)}, ${data.lagePremieTermijn}`
          : null,
    },
    {
      header: "(Hoge) premie, betalingstermijn",
      accessor: (data: Levensverzekering) =>
        data.hogePremie
          ? `${bedragFormat(data.hogePremie)}, ${data.hogePremieTermijn}`
          : null,
    },
    {
      header: "Einddatum premiebetaling, duur",
      accessor: (data: Levensverzekering) => {
        return `${getFormattedDateFromString(
          data.einddatumPremiebetaling
        )}, ${formatDuration(data.premieLooptijdInMaanden)}`;
      },
    },
    {
      header: "Polis valt fiscaal in",
      accessor: "polisValtFiscaalIn",
    },
    {
      header: "Ingebrachte waarde",
      accessor: "ingebrachteWaarde",
      dataType: "currency",
    },
    {
      header: "Actueel saldo premiedepot",
      accessor: "actueelSaldoPremiedepot",
      dataType: "currency",
    },
    {
      header: "Polis is verpand aan geldverstrekker",
      accessor: (data: Levensverzekering) =>
        data.polisIsVerpandAanGeldverstrekker ? "Ja" : "Nee",
    },
    {
      header: "Uitkering bedoeld voor aflossing hypotheek",
      accessor: (data: Levensverzekering) =>
        data.uitkeringBedoeldVoorAflossing ? "Ja" : "Nee",
    },
    {
      header: "Lijfrenteclausule",
      accessor: (data: Levensverzekering) =>
        data.lijfrenteclausule ? "Ja" : "Nee",
    },
    {
      header: "Beschikbare koopsom",
      accessor: "lijfrenteBeschikbareKoopsom",
      dataType: "currency",
    },
    {
      header: "Uitkering valt fiscaal in",
      accessor: "lijfrenteBox",
    },
    {
      header: "Gehanteerde lijfrentetarief",
      accessor: "lijfrenteTarief",
      dataType: "percentage",
    },
    {
      header: "Overgang op 2e verzekerde",
      accessor: "lijfrenteOvergangOp2eVerzekerde",
    },
    {
      header: "Duur lijfrente-uitkering",
      accessor: "lijfrenteDuur",
    },
    {
      header: "Einddatum hoge lijfrente-uitkering",
      accessor: "lijfrenteEinddatumHoog",
      dataType: "date",
    },
    {
      header: `Hoge lijfrente uitkering per ${levensverzekering.lijfrenteUitkeringstermijn}`,
      accessor: "uitkeringHoog",
      dataType: "currency",
    },
    {
      header: `${levensverzekering.lijfrenteEinddatumOmschrijving}`,
      accessor: "lijfrenteEinddatum",
      dataType: "date",
    },
    {
      header: `${levensverzekering.lijfrenteUitkeringOmschrijving} per ${levensverzekering.lijfrenteUitkeringstermijn}`,
      accessor: "lijfrenteUitkering",
      dataType: "currency-with-decimals",
    }
  );
  return cols;
};
